/**
 * Shared axios instance for the standalone MP (marketing-partner) app.
 *
 * Standalone change vs. the source:
 *  - The source branched the Authorization token on `pathname.startsWith('/mp/')`,
 *    preferring the partner token on /mp routes and the main token elsewhere.
 *    This app is partner-only, so it always attaches the partner token.
 *  - The source response interceptor refreshed the *main* session token
 *    (`/auth/refreshtoken` → `serviceToken`). That is main-app behaviour and
 *    never applied to partner tokens, so it is removed. Partner session
 *    expiry is handled by PartnerAuthContext (idle timeout + forcelogoutFromMp).
 */

import axios, { AxiosRequestConfig } from 'axios';
import { PARTNER_TOKEN_KEY } from './sessionCoordinator';

const axiosServices = axios.create({
    baseURL: import.meta.env.VITE_APP_API_URL || 'http://localhost:3010/',
    withCredentials: true
});

axiosServices.interceptors.request.use(
    async (config) => {
        const accessToken = localStorage.getItem(PARTNER_TOKEN_KEY);
        if (accessToken) {
            config.headers['Authorization'] = `Bearer ${accessToken}`;
        }
        return config;
    },
    (error) => Promise.reject(error)
);

axiosServices.interceptors.response.use(
    (response) => response,
    (error) => Promise.reject(error)
);

export default axiosServices;

export const fetcher = async (args: string | [string, AxiosRequestConfig]) => {
    const [url, config] = Array.isArray(args) ? args : [args];
    const res = await axiosServices.get(url, { ...config });
    return res.data;
};
